import React, { useEffect, useMemo, useRef, useState } from "react";
import { adminApi, API } from "@/lib/lnlApi";
import { useAuth } from "@/lib/lnlAuth";
import { toast } from "sonner";
import { Send, Users, MessageCircle, Plus, Search as SearchIcon, X, Circle, Hash, Paperclip, FileText, Image as ImageIcon } from "lucide-react";

function wsUrl() {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const ws = backendUrl.replace(/^http/i, (m) => (m.toLowerCase() === "https" ? "wss" : "ws"));
    return `${ws}/api/chat/ws`;
}

function fmtErr(e) {
    const d = e?.response?.data?.detail;
    if (typeof d === "string") return d;
    return d?.msg || e?.message || "Something went wrong.";
}

function fmtTime(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const today = new Date();
    const isToday = d.toDateString() === today.toDateString();
    return isToday
        ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
        : d.toLocaleDateString([], { month: "short", day: "numeric" });
}

export default function TeamChatPanel() {
    const [rooms, setRooms] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [users, setUsers] = useState([]);
    const [dialog, setDialog] = useState(null); // 'direct' | 'group' | null
    const [loading, setLoading] = useState(true);
    const [presence, setPresence] = useState({}); // user_id -> boolean
    const wsRef = useRef(null);
    const { user: me, token } = useAuth();
    const active = rooms.find((r) => r.id === activeId) || null;

    // ----- data loaders --------------------------------------------------
    async function loadRooms() {
        try {
            const [{ data: rs }, { data: us }] = await Promise.all([
                adminApi.get("/chat/rooms"),
                adminApi.get("/chat/users"),
            ]);
            setRooms(rs || []);
            setUsers(us || []);
            const pres = {};
            [...(rs || []).flatMap((r) => r.members || []), ...(us || [])].forEach((m) => {
                if (m?.id) pres[m.id] = !!m.online;
            });
            setPresence(pres);
        } catch (e) {
            toast.error(fmtErr(e));
        } finally {
            setLoading(false);
        }
    }

    async function loadMessages(roomId) {
        try {
            const { data } = await adminApi.get(`/chat/rooms/${roomId}/messages`, { params: { limit: 200 } });
            setMessages(data || []);
            await adminApi.post(`/chat/rooms/${roomId}/read`);
            setRooms((prev) => prev.map((r) => (r.id === roomId ? { ...r, unread: 0 } : r)));
        } catch (e) {
            toast.error(fmtErr(e));
        }
    }

    useEffect(() => { loadRooms(); }, []);
    useEffect(() => { if (activeId) loadMessages(activeId); /* eslint-disable-next-line */ }, [activeId]);

    // ----- WebSocket -----------------------------------------------------
    useEffect(() => {
        if (!token) return;
        const url = `${wsUrl()}?token=${encodeURIComponent(token)}`;
        let ws;
        let closed = false;
        let pingId;
        let reconnectId;

        function open() {
            ws = new WebSocket(url);
            wsRef.current = ws;
            ws.onopen = () => {
                pingId = setInterval(() => {
                    try { ws.send(JSON.stringify({ type: "ping" })); } catch { /* ignore */ }
                }, 25000);
            };
            ws.onmessage = (evt) => {
                let msg;
                try { msg = JSON.parse(evt.data); } catch { return; }
                if (msg.type === "message") {
                    // Append to current room, otherwise bump unread + last_message.
                    setRooms((prev) => {
                        const idx = prev.findIndex((r) => r.id === msg.room_id);
                        if (idx < 0) { loadRooms(); return prev; }
                        const copy = [...prev];
                        const r = { ...copy[idx] };
                        r.last_message_at = msg.message.created_at;
                        r.last_message = {
                            body: msg.message.body.slice(0, 140),
                            sender_id: msg.message.sender_id,
                            sender_name: msg.message.sender_name,
                            at: msg.message.created_at,
                        };
                        if (msg.message.sender_id !== me?.id && activeIdRef.current !== msg.room_id) {
                            r.unread = (r.unread || 0) + 1;
                        }
                        copy.splice(idx, 1);
                        copy.unshift(r);
                        return copy;
                    });
                    if (msg.room_id === activeIdRef.current) {
                        setMessages((prev) => [...prev, msg.message]);
                        adminApi.post(`/chat/rooms/${msg.room_id}/read`).catch(() => {});
                    }
                } else if (msg.type === "presence") {
                    setPresence((p) => ({ ...p, [msg.user_id]: msg.online }));
                } else if (msg.type === "room_update") {
                    setRooms((prev) => {
                        const idx = prev.findIndex((r) => r.id === msg.room.id);
                        if (idx < 0) return [msg.room, ...prev];
                        const copy = [...prev];
                        copy[idx] = msg.room;
                        return copy;
                    });
                }
            };
            ws.onclose = () => {
                if (pingId) clearInterval(pingId);
                if (!closed) reconnectId = setTimeout(open, 3000);
            };
            ws.onerror = () => { try { ws.close(); } catch { /* ignore */ } };
        }
        open();
        return () => {
            closed = true;
            if (pingId) clearInterval(pingId);
            if (reconnectId) clearTimeout(reconnectId);
            try { ws?.close(); } catch { /* ignore */ }
        };
        // eslint-disable-next-line
    }, [token]);

    // Keep a ref to activeId inside the WS handler.
    const activeIdRef = useRef(activeId);
    useEffect(() => { activeIdRef.current = activeId; }, [activeId]);

    async function sendMessage(body, file) {
        if (!activeId) return;
        if (!body?.trim() && !file) return;
        try {
            const fd = new FormData();
            fd.append("body", body || "");
            if (file) fd.append("attachment", file);
            const { data } = await adminApi.post(`/chat/rooms/${activeId}/messages`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            // Optimistic append (WS may also push — dedupe by id).
            setMessages((prev) => (prev.some((m) => m.id === data.id) ? prev : [...prev, data]));
        } catch (e) {
            toast.error(fmtErr(e));
        }
    }

    async function startDirect(userId) {
        try {
            const { data } = await adminApi.post("/chat/rooms/direct", { peer_id: userId });
            setRooms((prev) => (prev.some((r) => r.id === data.id) ? prev.map((r) => r.id === data.id ? data : r) : [data, ...prev]));
            setActiveId(data.id);
            setDialog(null);
        } catch (e) {
            toast.error(fmtErr(e));
        }
    }

    async function createGroup(name, memberIds) {
        try {
            const { data } = await adminApi.post("/chat/rooms/group", { name, member_ids: memberIds });
            setRooms((prev) => [data, ...prev.filter((r) => r.id !== data.id)]);
            setActiveId(data.id);
            setDialog(null);
        } catch (e) {
            toast.error(fmtErr(e));
        }
    }

    return (
        <div className="lnl-card overflow-hidden grid grid-cols-1 md:grid-cols-[280px_1fr] h-[calc(100vh-220px)] min-h-[500px]" data-testid="team-chat-panel">
            {/* Sidebar: rooms */}
            <div className="border-r border-lnl-border flex flex-col bg-lnl-off-white">
                <div className="p-3 border-b border-lnl-border flex items-center gap-2">
                    <div className="font-heading font-bold text-lnl-charcoal flex-1">Team Chat</div>
                    <button className="p-1.5 rounded-md hover:bg-white" title="New 1:1"
                        onClick={() => setDialog("direct")} data-testid="chat-new-direct">
                        <MessageCircle className="h-4 w-4 text-lnl-green" />
                    </button>
                    <button className="p-1.5 rounded-md hover:bg-white" title="New group"
                        onClick={() => setDialog("group")} data-testid="chat-new-group">
                        <Users className="h-4 w-4 text-lnl-green" />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto" data-testid="chat-rooms-list">
                    {loading ? (
                        <div className="p-4 text-sm text-lnl-gray">Loading…</div>
                    ) : rooms.length === 0 ? (
                        <div className="p-6 text-center text-sm text-lnl-gray">
                            No conversations yet.
                            <div className="mt-3 flex flex-col gap-1.5">
                                <button className="lnl-btn-secondary text-xs" onClick={() => setDialog("direct")}>
                                    <Plus className="h-3.5 w-3.5" /> Start a 1:1
                                </button>
                                <button className="lnl-btn-secondary text-xs" onClick={() => setDialog("group")}>
                                    <Plus className="h-3.5 w-3.5" /> Create a group
                                </button>
                            </div>
                        </div>
                    ) : (
                        rooms.map((r) => (
                            <RoomRow
                                key={r.id}
                                room={r}
                                active={r.id === activeId}
                                onClick={() => setActiveId(r.id)}
                                myId={me?.id}
                                presence={presence}
                            />
                        ))
                    )}
                </div>
            </div>

            {/* Right pane: messages */}
            <div className="flex flex-col min-w-0">
                {!active ? (
                    <div className="flex-1 grid place-items-center text-sm text-lnl-gray p-8 text-center">
                        <div>
                            <MessageCircle className="h-8 w-8 mx-auto text-lnl-green" />
                            <div className="mt-2 font-heading font-semibold text-lnl-charcoal">Pick a conversation</div>
                            <p className="text-xs text-lnl-gray max-w-sm mt-1">
                                Real-time chat with your fellow LNL staff. Start a 1:1 with anyone who has chat access, or create a group.
                            </p>
                        </div>
                    </div>
                ) : (
                    <RoomView
                        room={active}
                        messages={messages}
                        onSend={sendMessage}
                        myId={me?.id}
                        presence={presence}
                        token={token}
                    />
                )}
            </div>

            {dialog === "direct" && (
                <NewDirectDialog users={users} presence={presence} onPick={startDirect} onClose={() => setDialog(null)} />
            )}
            {dialog === "group" && (
                <NewGroupDialog users={users} onCreate={createGroup} onClose={() => setDialog(null)} />
            )}
        </div>
    );
}

function RoomRow({ room, active, onClick, myId, presence }) {
    const isDirect = room.type === "direct";
    const peer = isDirect ? (room.members || []).find((m) => m.id !== myId) : null;
    const online = peer ? !!presence[peer.id] : (room.members || []).some((m) => m.id !== myId && presence[m.id]);
    return (
        <button
            onClick={onClick}
            className={`w-full text-left px-3 py-2.5 border-b border-lnl-border flex gap-2.5 items-start hover:bg-white transition-colors ${active ? "bg-white" : ""}`}
            data-testid={`chat-room-${room.id}`}
        >
            <div className={`w-9 h-9 rounded-full grid place-items-center shrink-0 relative ${isDirect ? "bg-lnl-green-bg" : "bg-lnl-gold-soft/40"}`}>
                {isDirect ? (
                    <span className="text-xs font-heading font-bold text-lnl-green-deep">
                        {(peer?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
                    </span>
                ) : (
                    <Hash className="h-4 w-4 text-lnl-charcoal" />
                )}
                {isDirect && (
                    <Circle className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 ${online ? "text-emerald-500 fill-emerald-500" : "text-slate-300 fill-slate-300"}`} />
                )}
            </div>
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                    <div className="font-heading font-semibold text-sm text-lnl-charcoal truncate flex-1">{room.name}</div>
                    <div className="text-[10px] text-lnl-gray shrink-0">{fmtTime(room.last_message_at)}</div>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                    <div className="text-xs text-lnl-gray truncate flex-1">
                        {room.last_message ? (
                            <>
                                <span className="opacity-80">{room.last_message.sender_id === myId ? "You: " : ""}</span>
                                {room.last_message.body}
                            </>
                        ) : (
                            <span className="italic">no messages yet</span>
                        )}
                    </div>
                    {room.unread > 0 && (
                        <span className="text-[10px] font-heading font-bold bg-lnl-green text-white rounded-full px-1.5 py-0.5 min-w-[18px] text-center" data-testid={`chat-unread-${room.id}`}>
                            {room.unread > 99 ? "99+" : room.unread}
                        </span>
                    )}
                </div>
            </div>
        </button>
    );
}

function RoomView({ room, messages, onSend, myId, presence, token }) {
    const [text, setText] = useState("");
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const fileInputRef = useRef(null);
    const scrollRef = useRef(null);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, room.id]);

    // Cleanup blob preview URL on change/unmount.
    useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

    function pickFile(e) {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        const allowed = ["image/jpeg", "image/png", "image/webp", "image/gif", "application/pdf"];
        if (!allowed.includes(f.type)) {
            toast.error("Only images (JPG/PNG/WEBP/GIF) or PDFs are supported.");
            return;
        }
        if (f.size > 15 * 1024 * 1024) {
            toast.error("Attachment must be under 15MB.");
            return;
        }
        setFile(f);
        setPreview(f.type.startsWith("image/") ? URL.createObjectURL(f) : null);
    }

    function clearFile() {
        setFile(null);
        if (preview) URL.revokeObjectURL(preview);
        setPreview(null);
    }

    function submit(e) {
        e.preventDefault();
        const body = text.trim();
        if (!body && !file) return;
        onSend(body, file);
        setText("");
        clearFile();
    }

    const otherMembers = (room.members || []).filter((m) => m.id !== myId);
    const onlineOthers = otherMembers.filter((m) => presence[m.id]).length;

    return (
        <>
            <div className="px-4 py-3 border-b border-lnl-border flex items-center gap-3" data-testid="chat-header">
                <div className={`w-10 h-10 rounded-full grid place-items-center ${room.type === "direct" ? "bg-lnl-green-bg" : "bg-lnl-gold-soft/40"}`}>
                    {room.type === "direct" ? (
                        <span className="text-sm font-heading font-bold text-lnl-green-deep">
                            {(room.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
                        </span>
                    ) : (
                        <Hash className="h-5 w-5 text-lnl-charcoal" />
                    )}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="font-heading font-bold text-lnl-charcoal truncate">{room.name}</div>
                    <div className="text-xs text-lnl-gray">
                        {room.type === "direct"
                            ? (presence[otherMembers[0]?.id] ? "Online now" : "Offline")
                            : `${(room.members || []).length} members · ${onlineOthers} online`}
                    </div>
                </div>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-lnl-off-white/50" data-testid="chat-messages">
                {messages.length === 0 && (
                    <div className="text-center text-xs text-lnl-gray py-8">
                        No messages yet. Say hi 👋
                    </div>
                )}
                {messages.map((m, i) => {
                    const isMine = m.sender_id === myId;
                    const showHeader = i === 0 || messages[i - 1].sender_id !== m.sender_id;
                    return (
                        <div key={m.id} className={`flex ${isMine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
                            <div className={`max-w-[75%] rounded-lg px-3 py-2 ${isMine ? "bg-lnl-green text-white" : "bg-white border border-lnl-border text-lnl-charcoal"}`}>
                                {!isMine && showHeader && room.type === "group" && (
                                    <div className="text-[10px] font-heading font-bold text-lnl-green-deep mb-0.5">{m.sender_name}</div>
                                )}
                                {(m.attachments || []).map((att) => (
                                    <AttachmentView key={att.id} att={att} msgId={m.id} token={token} isMine={isMine} />
                                ))}
                                {m.body && <div className="text-sm whitespace-pre-wrap break-words">{m.body}</div>}
                                <div className={`text-[10px] mt-1 ${isMine ? "text-white/70" : "text-lnl-gray"}`}>
                                    {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
            {(file || preview) && (
                <div className="px-3 pt-2 border-t border-lnl-border bg-white flex items-center gap-2" data-testid="chat-attachment-preview">
                    {preview ? (
                        <img src={preview} alt="preview" className="h-14 w-14 rounded-md object-cover border border-lnl-border" />
                    ) : (
                        <div className="h-14 w-14 rounded-md border border-lnl-border grid place-items-center bg-lnl-off-white">
                            <FileText className="h-6 w-6 text-rose-700" />
                        </div>
                    )}
                    <div className="flex-1 min-w-0">
                        <div className="text-xs font-heading font-semibold text-lnl-charcoal truncate">{file?.name}</div>
                        <div className="text-[11px] text-lnl-gray">{Math.ceil((file?.size || 0) / 1024)} KB</div>
                    </div>
                    <button type="button" onClick={clearFile} className="p-1.5 rounded-md hover:bg-lnl-off-white" data-testid="chat-clear-attachment" aria-label="Remove attachment">
                        <X className="h-4 w-4" />
                    </button>
                </div>
            )}
            <form onSubmit={submit} className="p-3 border-t border-lnl-border flex gap-2 items-end" data-testid="chat-composer">
                <input type="file" ref={fileInputRef} accept="image/jpeg,image/png,image/webp,image/gif,application/pdf" onChange={pickFile} className="hidden" data-testid="chat-file-input" />
                <button type="button" onClick={() => fileInputRef.current?.click()} className="p-2 rounded-md hover:bg-lnl-off-white text-lnl-gray hover:text-lnl-charcoal" title="Attach photo or PDF" data-testid="chat-attach">
                    <Paperclip className="h-5 w-5" />
                </button>
                <textarea
                    rows={1}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(e); }
                    }}
                    placeholder="Type a message… (Shift+Enter for new line)"
                    className="flex-1 rounded-md border border-lnl-border px-3 py-2 text-sm resize-none max-h-32"
                    data-testid="chat-input"
                />
                <button type="submit" disabled={!text.trim() && !file} className="lnl-btn-primary text-sm" data-testid="chat-send">
                    <Send className="h-4 w-4" /> Send
                </button>
            </form>
        </>
    );
}

function AttachmentView({ att, msgId, token, isMine }) {
    const url = `${API}/chat/attachments/${msgId}/${att.id}?token=${encodeURIComponent(token || "")}`;
    if (att.is_image || (att.content_type || "").startsWith("image/")) {
        return (
            <a href={url} target="_blank" rel="noopener noreferrer" className="block mb-1 last:mb-0" data-testid={`chat-att-image-${att.id}`}>
                <img
                    src={url}
                    alt={att.filename}
                    className="rounded-md max-w-full max-h-56 object-cover border border-black/10"
                    loading="lazy"
                />
            </a>
        );
    }
    // PDF or other file
    return (
        <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className={`mb-1 last:mb-0 flex items-center gap-2 rounded-md px-2 py-1.5 text-xs ${isMine ? "bg-white/15 text-white hover:bg-white/25" : "bg-lnl-off-white text-lnl-charcoal hover:bg-lnl-green-bg"}`}
            data-testid={`chat-att-file-${att.id}`}
        >
            <FileText className="h-4 w-4 shrink-0" />
            <span className="truncate">{att.filename}</span>
            <span className={`text-[10px] ${isMine ? "text-white/70" : "text-lnl-gray"}`}>{Math.ceil((att.size || 0) / 1024)} KB</span>
        </a>
    );
}

function NewDirectDialog({ users, presence, onPick, onClose }) {
    const [q, setQ] = useState("");
    const filtered = useMemo(() => {
        const needle = q.toLowerCase().trim();
        return needle
            ? users.filter((u) => (u.name + " " + u.email + " " + (u.role || "")).toLowerCase().includes(needle))
            : users;
    }, [q, users]);
    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="dlg-new-direct">
                <div className="px-4 py-3 border-b border-lnl-border flex items-center gap-2">
                    <div className="font-heading font-bold text-lnl-charcoal flex-1">Start a 1:1 chat</div>
                    <button onClick={onClose} className="p-1 hover:bg-lnl-off-white rounded-md"><X className="h-4 w-4" /></button>
                </div>
                <div className="p-3 border-b border-lnl-border">
                    <div className="relative">
                        <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-lnl-gray" />
                        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search staff…"
                            className="w-full text-sm rounded-md border border-lnl-border pl-8 pr-3 py-2" data-testid="dlg-direct-search" />
                    </div>
                </div>
                <div className="overflow-y-auto flex-1">
                    {filtered.length === 0 && <div className="p-4 text-sm text-lnl-gray text-center">No matching staff with chat access.</div>}
                    {filtered.map((u) => (
                        <button key={u.id} onClick={() => onPick(u.id)}
                            className="w-full text-left px-3 py-2 border-b border-lnl-border flex items-center gap-2.5 hover:bg-lnl-off-white"
                            data-testid={`dlg-direct-user-${u.id}`}>
                            <div className="w-8 h-8 rounded-full bg-lnl-green-bg grid place-items-center relative shrink-0">
                                <span className="text-[11px] font-heading font-bold text-lnl-green-deep">
                                    {(u.name || u.email).split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
                                </span>
                                <Circle className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 ${presence[u.id] ? "text-emerald-500 fill-emerald-500" : "text-slate-300 fill-slate-300"}`} />
                            </div>
                            <div className="min-w-0">
                                <div className="text-sm font-heading font-semibold text-lnl-charcoal truncate">{u.name}</div>
                                <div className="text-[11px] text-lnl-gray truncate">{u.email} · {u.role}</div>
                            </div>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

function NewGroupDialog({ users, onCreate, onClose }) {
    const [name, setName] = useState("");
    const [selected, setSelected] = useState(new Set());
    function toggle(id) {
        setSelected((prev) => {
            const s = new Set(prev);
            if (s.has(id)) s.delete(id); else s.add(id);
            return s;
        });
    }
    function submit() {
        if (!name.trim()) { toast.error("Enter a group name."); return; }
        if (selected.size < 1) { toast.error("Select at least one member."); return; }
        onCreate(name.trim(), Array.from(selected));
    }
    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="dlg-new-group">
                <div className="px-4 py-3 border-b border-lnl-border flex items-center gap-2">
                    <div className="font-heading font-bold text-lnl-charcoal flex-1">Create a group</div>
                    <button onClick={onClose} className="p-1 hover:bg-lnl-off-white rounded-md"><X className="h-4 w-4" /></button>
                </div>
                <div className="p-3 border-b border-lnl-border">
                    <label className="text-xs font-heading font-semibold text-lnl-gray">Group name</label>
                    <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Admissions team"
                        className="mt-1 w-full text-sm rounded-md border border-lnl-border px-3 py-2" data-testid="dlg-group-name" />
                </div>
                <div className="overflow-y-auto flex-1 max-h-64">
                    {users.map((u) => (
                        <label key={u.id} className="flex items-center gap-2.5 px-3 py-2 border-b border-lnl-border hover:bg-lnl-off-white cursor-pointer" data-testid={`dlg-group-user-${u.id}`}>
                            <input type="checkbox" checked={selected.has(u.id)} onChange={() => toggle(u.id)} className="shrink-0" />
                            <div className="min-w-0">
                                <div className="text-sm font-heading font-semibold text-lnl-charcoal truncate">{u.name}</div>
                                <div className="text-[11px] text-lnl-gray truncate">{u.email} · {u.role}</div>
                            </div>
                        </label>
                    ))}
                </div>
                <div className="p-3 border-t border-lnl-border flex items-center justify-between">
                    <div className="text-xs text-lnl-gray">{selected.size} selected</div>
                    <button onClick={submit} className="lnl-btn-primary text-sm" data-testid="dlg-group-create">
                        <Plus className="h-4 w-4" /> Create group
                    </button>
                </div>
            </div>
        </div>
    );
}
